import React, { useCallback, useEffect, useState } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import { Link, Paragraph } from '@rover/kibble/core';
import Modal from '@rover/kibble/official-patterns/Modal';
import type { CountryConfigurationUrls } from '@rover/rsdk/src/apiClient/latest/model/countryConfigurationUrls';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type { MarketingOptInIntent } from '@rover/types/src/MarketingOptIn';

import { MarketingOptInView } from './analyticsEvents';

type Props = {
  shouldShowMarketingOptInModal: boolean;
  submitMarketingOptInIntent: (notification: MarketingOptInIntent) => void;
  currentUrls?: CountryConfigurationUrls;
};

const MarketingOptIn: React.FC<Props> = (props) => {
  const { shouldShowMarketingOptInModal, submitMarketingOptInIntent, currentUrls } = props;
  const [showModal, setShowModal] = useState(false);

  const turnNotificationOn = useCallback(
    (value: boolean) => {
      submitMarketingOptInIntent({ turnNotificationsOn: value });
      setShowModal(false);
    },
    [submitMarketingOptInIntent]
  );

  useEffect(() => {
    if (showModal) {
      emitAnalyticsEvent(new MarketingOptInView());
    }
  }, [showModal]);

  useEffect(() => {
    if (shouldShowMarketingOptInModal) {
      setShowModal(true);
    }
  }, [shouldShowMarketingOptInModal]);

  return (
    <Modal
      isOpen={showModal}
      headerText={t({
        message: `Stay updated`,
        comment:
          'The title of a modal that will prompt the user to opt-in to email marketing emails',
      })}
      submitButtonText={t`Accept`}
      cancelButtonText={t`Decline`}
      onRequestClose={() => {}}
      onClickCancel={() => turnNotificationOn(false)}
      onClickSubmit={() => turnNotificationOn(true)}
      showCloseIcon={false}
      aria-modal
    >
      <Paragraph>
        <Trans>
          Yes, I want to receive marketing emails and messages from Rover and its affiliates. I can
          unsubscribe at any time.{' '}
          <Link href={currentUrls?.privacyPolicyUrl ?? '/terms/privacy'} target="_blank">
            Privacy Statement.
          </Link>
        </Trans>
      </Paragraph>
    </Modal>
  );
};

export default MarketingOptIn;
